import { useEffect, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  BrowserMockFrame,
  buildRegistrationHref,
  ProductScreenshot,
  PublicLpFooter,
  PublicLpHeader,
  SectionIntro,
} from "../components/public/HypothesisLandingParts";
import {
  ProductCatalogContentUnavailable,
  type ProductCatalogViewState,
} from "../components/public/ProductCatalogContent";
import { useProductCatalog } from "../hooks/useProductCatalog";
import { getProductCatalogPlan, getProductCatalogQuotaDisplayLabel } from "../lib/productCatalog";
import { buildPublicLpDestinationHref, buildPublicLpReturnTo } from "../lib/publicLpReturn";
import { PUBLIC_PAGE_METADATA } from "../publicPageMetadata";

const PRESIDENT_CONSULT_METADATA = PUBLIC_PAGE_METADATA.presidentConsult;

const HEADER_NAVIGATION = [
  { href: "#workflow", label: "使い方" },
  { href: "#kabeuchi", label: "AI壁打ち" },
  { href: "#president-room", label: "AI社長室とは" },
  { href: "#pricing", label: "料金" },
] as const;

const PAINS = [
  {
    number: "01",
    title: "社員には話しにくいことがある",
    body: "人事、組織、経営判断。決める前だからこそ、まだ社内には出しにくい話もあります。",
  },
  {
    number: "02",
    title: "専門家へ相談するほどではない",
    body: "ちょっと意見がほしいだけなのに、毎回誰かへ相談するほどでもない。",
  },
  {
    number: "03",
    title: "一人だと同じところを回る",
    body: "考えているつもりでも、気づけば同じ前提の中で悩み続けてしまう。",
  },
  {
    number: "04",
    title: "違う見方も聞いてから決めたい",
    body: "賛成だけではなく、慎重な見方や厳しい意見も聞いたうえで判断したい。",
  },
] as const;

const STANCE_EXAMPLES = [
  ["慎重", "リスクを大きめに見る"],
  ["数字重視", "数字や条件から考える"],
  ["懐疑派", "前提そのものを疑ってみる"],
  ["逆張り", "あえて反対側から考える"],
  ["発想派", "別の可能性を広げる"],
] as const;

const PRESIDENT_ROOM_FEATURES = [
  "日々のAI相談",
  "AI壁打ち",
  "企業リサーチ",
  "ナレッジライブラリ",
  "ニュース・レポート",
  "カレンダー予定確認",
] as const;

function HeroConsultVisual() {
  const steps = [
    { label: "話す", body: "相談したいことを入力", accent: "bg-[#F4DED0] text-[#8A4F35]" },
    { label: "選ぶ", body: "相談相手とスタンスを決める", accent: "bg-[#E9E2C8] text-[#695C2D]" },
    { label: "壁打ち", body: "選んだ構成で意見を聞く", accent: "bg-[#DCE8E2] text-[#3D6958]" },
  ] as const;

  return (
    <div className="relative rounded-[2rem] border border-[#DEC7B6] bg-white/90 p-4 shadow-[0_26px_60px_rgba(112,72,52,0.18)] sm:p-6">
      <div className="flex items-center gap-2 border-b border-[#EEE1D5] pb-4">
        <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-[#D9826F]" />
        <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-[#E5B96C]" />
        <span aria-hidden className="h-2.5 w-2.5 rounded-full bg-[#82AE92]" />
        <span className="ml-2 text-xs font-black tracking-[0.12em] text-[#8A756B]">PRESIDENT CONSULT</span>
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_auto_1fr_auto_1fr] sm:items-stretch">
        {steps.map((step, index) => (
          <div key={step.label} className="contents">
            <div className="rounded-2xl border border-[#E9D9CC] bg-[#FFFDFC] p-4">
              <span className={`inline-flex rounded-full px-3 py-1 text-xs font-black ${step.accent}`}>{step.label}</span>
              <p className="mt-3 text-sm font-black leading-6 text-[#554039]">{step.body}</p>
            </div>
            {index < steps.length - 1 ? <span aria-hidden className="hidden self-center text-xl font-black text-[#C6A58F] sm:block">→</span> : null}
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-2xl bg-[#5C443A] px-5 py-4 text-white">
        <p className="text-xs font-black tracking-[0.14em] text-[#F0BEA5]">AI社長室 WizMate</p>
        <p className="mt-2 text-sm font-bold leading-6 text-[#F8EDE6]">相談内容からテーマとメンバー候補を確認し、選んだ構成で一緒に考えます。</p>
      </div>
    </div>
  );
}

function ConsultationStartMock() {
  const examples = ["値上げを検討したい", "採用について考えたい", "新規事業に迷っている"] as const;

  return (
    <div>
      <div>
        <p className="text-sm font-black text-[#4E3830]">相談内容からおすすめ</p>
        <p className="mt-1 text-xs leading-5 text-[#75625B]">悩みをそのまま入力して始められます。</p>
      </div>
      <div className="mt-3 min-h-24 rounded-2xl border border-[#E4D0C1] bg-[#FFFDFC] px-4 py-3 text-sm leading-6 text-[#5F4A42] shadow-inner">
        値上げしたいけど、取引先が離れないか心配で……。
      </div>
      <div className="mt-3 flex flex-wrap gap-2" aria-label="相談例のチップ">
        {examples.map((example) => (
          <span key={example} className="rounded-full border border-[#E4D0C1] bg-white px-3 py-1.5 text-[11px] font-bold text-[#6B5851]">{example}</span>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[#F2ECE7] px-4 py-3">
        <div>
          <p className="text-xs font-black text-[#5D4037]">参考資料を添付</p>
          <p className="mt-1 text-[11px] text-[#78645C]">必要ならナレッジを選べます</p>
        </div>
        <span className="rounded-full border border-[#D1B9A9] bg-white px-3 py-1 text-[11px] font-black text-[#8A4F35]">ナレッジを選ぶ</span>
      </div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <span className="rounded-full bg-[#5D4D46] px-4 py-2 text-center text-xs font-black text-white">おすすめメンバーを確認</span>
        <span className="rounded-full border border-[#D9C5B9] bg-white px-4 py-2 text-center text-xs font-black text-[#6B5851]">自由に選ぶ</span>
      </div>
      <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-[#E4D0C1] bg-[#FFFDFC] px-4 py-3">
        <div>
          <p className="text-xs font-black text-[#5D4037]">続きから始める</p>
          <p className="mt-1 text-[11px] text-[#78645C]">以前の相談を開く</p>
        </div>
        <span className="text-xs font-black text-[#8A4F35]">進む →</span>
      </div>
    </div>
  );
}

function MemberSelectionMock() {
  const members = [
    { name: "メイ", role: "ファシリテーター", stance: "固定" },
    { name: "CFO", role: "数字と資金面を見る", stance: "数字重視" },
    { name: "COO", role: "運用と実行を見る", stance: "慎重" },
  ] as const;

  return (
    <div>
      <div className="rounded-2xl border border-[#E7D6CA] bg-[#FFF8F3] p-4">
        <span className="rounded-full bg-[#F4DED0] px-3 py-1 text-[10px] font-black text-[#8A4F35]">おすすめテーマ</span>
        <p className="mt-3 text-sm font-black text-[#4E3830]">値上げの進め方を整理する</p>
        <p className="mt-2 text-xs leading-5 text-[#75625B]"><strong className="text-[#8A4F35]">おすすめ理由：</strong>数字と運用の両面から条件や進め方を確認できる構成です。</p>
      </div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="mt-4 text-sm font-black text-[#4E3830]">参加メンバーを確認する</p>
          <p className="mt-1 text-xs leading-5 text-[#7B6861]">開始前にメンバーとスタンスを調整</p>
        </div>
        <span className="rounded-full bg-[#F4DED0] px-3 py-1 text-[10px] font-black text-[#8A4F35]">おすすめ</span>
      </div>
      <div className="mt-4 space-y-3">
        {members.map((member) => (
          <div key={member.name} className="rounded-2xl border border-[#E7D6CA] bg-[#FFFDFC] p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-black text-[#4E3830]">{member.name}</p>
                <p className="mt-1 text-xs text-[#75625B]">{member.role}</p>
              </div>
              <span className="rounded-full bg-[#E8F0EB] px-3 py-1 text-xs font-black text-[#3D6958]">{member.stance}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-2" aria-label="スタンス選択イメージ">
        {["強気", "慎重", "発想派", "現実派", "数字重視", "ユーザー至上", "懐疑派", "逆張り"].map((stance) => (
          <span key={stance} className="rounded-full border border-[#DCC6B4] bg-white px-2.5 py-1 text-[11px] font-bold text-[#6B5851]">{stance}</span>
        ))}
      </div>
      <div className="mt-4 rounded-xl border border-dashed border-[#CBAF9E] px-4 py-3 text-center text-xs font-black text-[#8A4F35]">＋ メンバーを追加する</div>
      <div className="mt-3 rounded-full bg-[#5D4D46] px-4 py-2.5 text-center text-xs font-black text-white">このメンバーで始める</div>
    </div>
  );
}

function WallDiscussionStartMock() {
  const members = [
    ["メイ", "進行"],
    ["CFO", "数字重視"],
    ["COO", "慎重"],
  ] as const;

  return (
    <div>
      <p className="text-xs font-black tracking-[0.12em] text-[#9A5D42]">開始前に選んだ構成</p>
      <div className="mt-3 rounded-2xl border border-[#E5D4C8] bg-[#FFFDFC] px-4 py-3">
        <p className="text-[11px] font-black text-[#9B5E43]">相談テーマ</p>
        <p className="mt-1 text-sm font-black leading-6 text-[#5F4A42]">値上げの進め方を整理する</p>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {members.map(([name, stance]) => (
          <div key={name} className="rounded-xl bg-[#F4ECE5] px-3 py-3 text-center">
            <p className="text-xs font-black text-[#5D4037]">{name}</p>
            <p className="mt-1 text-[11px] font-bold text-[#80675C]">{stance}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-full bg-[#5D4D46] px-4 py-3 text-center text-sm font-black text-white">壁打ちを始める</div>
      <div className="mt-4 rounded-xl bg-[#E8F0EB] px-4 py-3">
        <p className="text-xs font-black text-[#355849]">開始後のイメージ</p>
        <p className="mt-1 text-xs leading-5 text-[#557366]">選んだ役割とスタンスから、同じテーマへの意見を聞きます。</p>
      </div>
    </div>
  );
}

function WorkflowStep({
  number,
  title,
  children,
  visual,
  reverse = false,
}: {
  number: string;
  title: string;
  children: ReactNode;
  visual: ReactNode;
  reverse?: boolean;
}) {
  return (
    <article className="grid items-center gap-8 rounded-[2rem] border border-[#E8D8CC] bg-white/75 p-5 shadow-[0_16px_42px_rgba(102,72,56,0.09)] md:p-8 lg:grid-cols-2 lg:gap-12">
      <div className={reverse ? "lg:order-2" : ""}>
        <p className="text-xs font-black tracking-[0.2em] text-[#B06E4F]">STEP {number}</p>
        <h3 className="mt-3 text-2xl font-black leading-snug text-[#4E3830] md:text-3xl">{title}</h3>
        <div className="mt-5 space-y-4 text-base leading-8 text-[#6B5851]">{children}</div>
      </div>
      <div className={reverse ? "lg:order-1" : ""}>{visual}</div>
    </article>
  );
}

export function PresidentConsultPageView({
  catalog,
  catalogLoading = false,
  retryCatalog,
}: ProductCatalogViewState) {
  const location = useLocation();
  const registrationHref = buildRegistrationHref(location.search);
  const returnTo = buildPublicLpReturnTo(location.pathname, location.search);
  const freePlan = catalog ? getProductCatalogPlan(catalog, "free") : null;
  const standardPlan = catalog ? getProductCatalogPlan(catalog, "standard") : null;
  const consultationLimits = catalog ? {
    free: {
      chat: getProductCatalogQuotaDisplayLabel(catalog, "free", "chat", "monthly_messages"),
      groupTalk: getProductCatalogQuotaDisplayLabel(catalog, "free", "group_talk", "monthly_turns"),
    },
    standard: {
      chat: getProductCatalogQuotaDisplayLabel(catalog, "standard", "chat", "monthly_messages"),
      groupTalk: getProductCatalogQuotaDisplayLabel(catalog, "standard", "group_talk", "monthly_turns"),
    },
  } : null;
  const pricingReady = Boolean(
    freePlan
    && standardPlan
    && consultationLimits?.free.chat
    && consultationLimits.free.groupTalk
    && consultationLimits.standard.chat
    && consultationLimits.standard.groupTalk
  );

  useEffect(() => {
    document.title = PRESIDENT_CONSULT_METADATA.title;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    description?.setAttribute("content", PRESIDENT_CONSULT_METADATA.description);
  }, []);

  return (
    <div className="h-dvh overflow-x-hidden overflow-y-auto scroll-smooth bg-[#FDF8F5] text-[#4E3830]">
      <PublicLpHeader
        registrationHref={registrationHref}
        navigation={HEADER_NAVIGATION}
        navigationLabel="社長相談LP"
      />
      <main>
        <section className="relative overflow-hidden">
          <div aria-hidden className="absolute -left-24 top-8 h-72 w-72 rounded-full bg-[#F3D8C8]/60 blur-3xl" />
          <div aria-hidden className="absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-[#DEEAE3]/70 blur-3xl" />
          <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-5 pb-16 pt-12 md:px-8 md:pb-24 md:pt-20 lg:grid-cols-[1.02fr_0.98fr]">
            <div>
              <p className="inline-flex rounded-full border border-[#DCB9A6] bg-white/70 px-4 py-2 text-xs font-black tracking-[0.12em] text-[#9A563A]">社長の相談相手を、いつでもそばに</p>
              <h1 className="mt-6 text-4xl font-black leading-[1.25] tracking-tight text-[#49332B] md:text-5xl lg:text-[3rem]">
                社長の「ちょっと相談したい」に、<br className="hidden md:block" /><span className="text-[#A05F3F]">いつでも相手がいる。</span>
              </h1>
              <div className="mt-6 space-y-3 text-base leading-8 text-[#68554D] md:text-lg">
                <p>採用するか。値上げするか。新しい事業を始めるか。社長には、正解のない判断が毎日のようにあります。</p>
                <p>でも、社員には話しにくい。専門家へ相談するほどではない。一人で考えていると、同じところをぐるぐる回る。</p>
                <p>WizMateなら、まず相談したいことをそのまま入力。</p>
                <p>相談内容に合ったAIメンバーを選び、いろいろな立場から意見を聞きながら考えを整理します。</p>
              </div>
              <p className="mt-6 border-l-4 border-[#C27A58] pl-4 text-sm font-black leading-7 text-[#5A453D]">WizMateは、社長の仕事を支える「AI社長室」です。</p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link to={registrationHref} className="inline-flex min-h-14 items-center justify-center rounded-2xl bg-[#8A4F35] px-7 text-base font-black text-white shadow-[0_14px_30px_rgba(138,79,53,0.25)] transition hover:bg-[#74412C] focus:outline-none focus:ring-2 focus:ring-[#C58B6B] focus:ring-offset-2">無料でWizMateを試す</Link>
                <a href="#workflow" className="inline-flex min-h-12 items-center justify-center px-4 text-sm font-black text-[#8A4F35] underline decoration-[#D0A18A] underline-offset-4">相談の流れを見る ↓</a>
              </div>
            </div>
            <HeroConsultVisual />
          </div>
        </section>

        <section className="border-y border-[#EADBC8] bg-[#F8EFE8]">
          <div className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24">
            <SectionIntro eyebrow="WHEN YOU NEED TO TALK" title="社長だから、誰にでも相談できるわけではない。" align="center" />
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {PAINS.map((pain) => (
                <article key={pain.number} className="rounded-[1.5rem] border border-[#E1CFC1] bg-white p-5 shadow-[0_12px_28px_rgba(102,72,56,0.07)]">
                  <span className="text-xs font-black text-[#BC7A5B]">{pain.number}</span>
                  <h3 className="mt-3 text-lg font-black leading-7 text-[#4E3830]">{pain.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-[#705D55]">{pain.body}</p>
                </article>
              ))}
            </div>
            <p className="mx-auto mt-9 max-w-4xl text-center text-lg font-black leading-8 text-[#5D4037]">答えを決めてもらうのではなく、自分で決めるために考える相手を増やします。</p>
          </div>
        </section>

        <section id="workflow" className="scroll-mt-20">
          <div className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24">
            <SectionIntro eyebrow="3 STEPS" title="まとまっていなくても、相談は始められる。" align="center">
              <p>相談内容を入力して、相談相手とスタンスを決め、壁打ちする。WizMateを使った経営相談の流れです。</p>
            </SectionIntro>
            <div className="mt-12 space-y-7">
              <WorkflowStep number="1" title="相談したいことを入力する" visual={<BrowserMockFrame title="相談したいことを入力する" caption="入力・チップ・参考資料から相談を始め、過去の相談も再開できます。"><ConsultationStartMock /></BrowserMockFrame>}>
                <p>相談したいことは、きれいにまとめなくても、そのまま入力できます。</p>
                <p>入力例のチップから選んで始めることも、必要なナレッジを参考資料として添付することもできます。</p>
                <p>以前の相談は、<strong className="text-[#4E3830]">「続きから始める」から再開できます。</strong></p>
                <p>入力した内容をもとに、AIが相談テーマを整理し、次の画面でおすすめ理由とメンバーを提示します。</p>
              </WorkflowStep>

              <WorkflowStep number="2" title="参加メンバーとスタンスを決める" reverse visual={<BrowserMockFrame title="参加メンバーを確認する" caption="おすすめをもとに、開始前にメンバーとスタンスを調整します。"><MemberSelectionMock /></BrowserMockFrame>}>
                <p>次の画面では、おすすめテーマ・おすすめ理由と、相談内容に合ったAIメンバーが表示されます。</p>
                <p>おすすめされたメンバーでそのまま始めても、自分で参加メンバーを追加・削除してもOK。</p>
                <p>さらに、<strong className="text-[#4E3830]">開始前にメンバーごとのスタンスも選べます。</strong></p>
                <p>反対意見や批判的な見方を聞きたいときも、ここでメンバーやスタンスを調整します。<strong className="text-[#4E3830]">調整するのは壁打ちを始める前です。</strong></p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {STANCE_EXAMPLES.map(([name, description]) => (
                    <div key={name} className="rounded-2xl bg-[#F4ECE5] p-4">
                      <h4 className="font-black text-[#5D4037]">{name}</h4>
                      <p className="mt-1 text-sm leading-6 text-[#6B5851]">{description}</p>
                    </div>
                  ))}
                </div>
                <p className="rounded-2xl border-l-4 border-[#B87352] bg-white px-5 py-4 text-lg font-black leading-8 text-[#5D4037]">「今日は厳しい意見も聞きたい」を、相談前に設定できます。</p>
              </WorkflowStep>

              <WorkflowStep number="3" title="選んだメンバーと壁打ちする" visual={<BrowserMockFrame title="壁打ちを始める" caption="開始前に決めたメンバーとスタンスで、壁打ちを始めます。"><WallDiscussionStartMock /></BrowserMockFrame>}>
                <p>STEP2で決めたメンバー・スタンスを確認し、<strong className="text-[#4E3830]">「このメンバーで始める」から壁打ちへ進みます。</strong></p>
                <p>実際にどんな意見が返るかは、次のAI壁打ちセクションで紹介します。</p>
              </WorkflowStep>
            </div>
          </div>
        </section>

        <section id="kabeuchi" className="scroll-mt-20 bg-[#5C443A] text-white">
          <div className="mx-auto grid max-w-6xl gap-10 px-5 py-16 md:px-8 md:py-24 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <p className="text-xs font-black tracking-[0.2em] text-[#F0BEA5]">AI KABEUCHI</p>
              <h2 className="mt-3 text-3xl font-black leading-tight md:text-4xl">賛成してもらうためではなく、考えを深めるために。</h2>
              <div className="mt-6 space-y-4 text-base leading-8 text-[#F3E7E0]">
                <p>同じテーマを複数のAIメンバーが、それぞれの役割から見て意見を出します。</p>
                <p>財務、運用、技術など役割によって見るポイントが変わり、開始前に選んだスタンスによっても着目点が変わります。</p>
                <p><strong className="text-white">慎重、数字重視、懐疑派、逆張り</strong>など、聞きたい角度を開始前に選んで相談できます。</p>
              </div>
            </div>
            <ProductScreenshot src="/img/sales-support-ai-branching.jpg" alt="GroupTalk実画面。ユーザーの発言に対してメイ、CFO、COO、弁護士が異なる立場から回答" width={802} height={1066} caption="開始前に選んだ役割とスタンスから、同じ相談への意見を受け取れます。" frameTitle="AI壁打ちルーム" cropClassName="aspect-[21/20] overflow-hidden" figureClassName="mx-auto w-full max-w-2xl" />
          </div>
          <div className="mx-auto max-w-4xl px-5 pb-16 text-center text-lg font-black leading-8 text-[#F8EDE6] md:px-8 md:pb-24">
            <p>最後に決めるのは社長。</p>
            <p className="mt-2">その前に、一度いろいろな角度から考える。</p>
          </div>
        </section>

        <section>
          <div className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24">
            <SectionIntro eyebrow="YOUR KNOWLEDGE" title="資料を見ながら相談することも。">
              <p>事業計画、提案資料、社内資料などのPDFや、Webページをナレッジライブラリへ登録して、壁打ちの材料にできます。</p>
              <p className="mt-4">紙でもらった資料や手書きメモは、スマホで撮影して「社長メモ」に。</p>
              <p className="mt-4"><strong className="text-[#4E3830]">頭の中だけでなく、実際の資料を見ながら相談できます。</strong></p>
            </SectionIntro>
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              {["PDF", "社長メモ", "Webページ"].map((format) => (
                <div key={format} className="rounded-[1.4rem] border border-[#E2CFC0] bg-white p-5 text-center font-black text-[#5D4037] shadow-[0_10px_24px_rgba(88,61,47,0.07)]">{format}</div>
              ))}
            </div>
          </div>
        </section>

        <section id="president-room" className="scroll-mt-20 bg-[#E8F0EB]">
          <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 md:px-8 md:py-24 lg:grid-cols-[1fr_0.85fr]">
            <SectionIntro eyebrow="AI PRESIDENT'S OFFICE" title="相談相手だけではなく、社長の仕事を支えるAIへ。">
              <p>相談が終われば、また別の仕事が始まります。</p>
              <p className="mt-4">相談する、調べる、資料を使う、考えを整理する。</p>
              <p className="mt-4">社長の日々の仕事を支える。それが「AI社長室 WizMate」です。</p>
            </SectionIntro>
            <div className="rounded-[2rem] bg-white p-6 shadow-[0_18px_42px_rgba(56,91,74,0.13)]">
              <p className="text-center text-xs font-black tracking-[0.18em] text-[#4E7C67]">AI社長室 WizMate</p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                {PRESIDENT_ROOM_FEATURES.map((item) => <div key={item} className="rounded-xl bg-[#F0F6F2] p-3 text-center text-xs font-bold leading-5 text-[#3F5E51]">{item}</div>)}
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24">
            <div className="rounded-[2rem] border border-[#E2CFC0] bg-white px-6 py-9 shadow-[0_15px_36px_rgba(102,72,56,0.08)] md:px-10 md:py-12">
              <SectionIntro eyebrow="TRUST" title="会社の資料をAIに入れても、大丈夫？" contentClassName="max-w-4xl">
                <p>事業計画や社内資料には、社外に出したくない情報もあります。</p>
                <p className="mt-3">WizMateは回答や分析に必要な範囲で外部AIサービスを利用しますが、<strong className="text-[#4E3830]">ユーザーの明示的な同意がない限り、入力した会話や資料を外部AI事業者の汎用モデル学習へ任意提供しません。</strong></p>
                <p className="mt-3">WizMate側でも、明示的な同意なく、会話や資料を独自モデルの学習・再学習に使いません。</p>
                <p className="mt-3"><strong className="text-[#4E3830]">AIに処理させるために送信した情報が、そのまま一般に公開されたり、他のユーザーへ共有されたりするものではありません。</strong></p>
                <p className="mt-3">詳しいデータの取り扱いは、利用規約・プライバシーポリシーで確認できます。</p>
              </SectionIntro>
              <div className="mt-6 flex flex-wrap gap-5 text-sm font-black text-[#8A4F35]">
                <Link to={buildPublicLpDestinationHref("/privacy", returnTo)} className="underline underline-offset-4">プライバシーポリシーを見る</Link>
                <Link to={buildPublicLpDestinationHref("/terms", returnTo)} className="underline underline-offset-4">利用規約を見る</Link>
              </div>
            </div>
          </div>
        </section>

        <section id="pricing" className="scroll-mt-20 bg-[#F8EFE8]">
          <div className="mx-auto max-w-5xl px-5 py-16 md:px-8 md:py-24">
            <SectionIntro
              eyebrow="PRICING"
              title={freePlan ? `まずは${freePlan.displayName}で、AI社長室を試す。` : "AI社長室に合うプランを確認する。"}
              align="center"
            >
              <p>AI相談とAI壁打ちルームの利用回数は、現在のプラン設定をもとに表示しています。</p>
            </SectionIntro>
            {pricingReady ? (
            <div className="mx-auto mt-10 grid max-w-4xl gap-5 md:grid-cols-2">
              {[
                { key: "free" as const, plan: freePlan!, limits: consultationLimits!.free, cta: "無料で始める" },
                { key: "standard" as const, plan: standardPlan!, limits: consultationLimits!.standard, cta: `まず${freePlan!.displayName}で試す` },
              ].map(({ key, plan, limits, cta }) => (
                <article key={key} className={`rounded-[1.8rem] border bg-white p-7 shadow-[0_16px_36px_rgba(102,72,56,0.1)] ${key === "standard" ? "border-[#B87352]" : "border-[#E1CFC1]"}`}>
                  {key === "standard" ? <span className="rounded-full bg-[#8A4F35] px-3 py-1 text-[11px] font-black text-white">相談・壁打ちを続けるなら</span> : null}
                  <h3 className="mt-4 text-xl font-black text-[#4E3830]">{plan.displayName}</h3>
                  <p className="mt-2 text-2xl font-black text-[#8A4F35]">{plan.price.displayLabel}</p>
                  <p className="mt-3 text-sm leading-6 text-[#75625B]">{plan.description}</p>
                  <dl className="mt-6 space-y-3 text-sm font-bold text-[#66534C]">
                    <div className="flex justify-between gap-4"><dt>通常チャット</dt><dd>{limits.chat}</dd></div>
                    <div className="flex justify-between gap-4"><dt>AI壁打ちルーム</dt><dd>{limits.groupTalk}</dd></div>
                  </dl>
                  <Link to={registrationHref} className={`mt-7 inline-flex min-h-12 w-full items-center justify-center rounded-xl px-5 text-sm font-black transition focus:outline-none focus:ring-2 focus:ring-[#C58B6B] focus:ring-offset-2 ${key === "standard" ? "bg-[#8A4F35] text-white hover:bg-[#74412C]" : "border border-[#B87352] text-[#8A4F35] hover:bg-[#FFF5EF]"}`}>{cta}</Link>
                </article>
              ))}
            </div>
            ) : (
              <div className="mx-auto mt-10 max-w-4xl">
                <ProductCatalogContentUnavailable
                  loading={catalogLoading}
                  retry={retryCatalog}
                  plansHref={buildPublicLpDestinationHref("/plans", returnTo)}
                />
              </div>
            )}
            <p className="mt-6 text-center text-xs leading-6 text-[#837068]">プランの全機能・注意事項は<Link to={buildPublicLpDestinationHref("/plans", returnTo)} className="ml-1 font-black text-[#8A4F35] underline underline-offset-4">料金ページ</Link>で確認できます。</p>
          </div>
        </section>

        <section className="px-5 py-16 md:px-8 md:py-24">
          <div className="relative mx-auto max-w-6xl overflow-hidden rounded-[2.2rem] bg-[linear-gradient(135deg,#714B3A_0%,#9A5D42_100%)] px-6 py-12 text-center text-white shadow-[0_24px_54px_rgba(107,67,48,0.25)] md:px-10 md:py-16">
            <div aria-hidden className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
            <h2 className="relative text-3xl font-black leading-tight md:text-4xl">一人で考え込む前に、WizMateに話してみませんか？</h2>
            <div className="relative mx-auto mt-6 max-w-2xl space-y-2 text-base leading-8 text-[#F4E7E0]">
              <p>まとまっていなくても、そのまま話す。</p>
              <p>相談相手を選ぶ。</p>
              <p>違う立場の意見を聞いて、自分の考えを整理する。</p>
              <p className="pt-2 text-lg font-black text-white">決める前の壁打ち相手を、AI社長室に。</p>
            </div>
            <Link to={registrationHref} className="relative mt-8 inline-flex min-h-14 items-center justify-center rounded-2xl bg-white px-8 text-base font-black text-[#7A4C39] shadow-xl transition hover:bg-[#FFF6F1] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#8A4F35]">無料でWizMateを試す</Link>
          </div>
        </section>
      </main>
      <PublicLpFooter returnTo={returnTo} />
    </div>
  );
}

export default function PresidentConsultPage() {
  const { catalog, loading, error, retry } = useProductCatalog();
  return (
    <PresidentConsultPageView
      catalog={catalog}
      catalogLoading={loading}
      catalogError={error}
      retryCatalog={retry}
    />
  );
}
